import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SITE_URL,
  SITE_TITLE,
  SITE_LOGO,
  DEFAULT_OG_IMAGE,
} from '@/config/site';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  pathname?: string;
  lang?: string;
  ogImage?: string;
  articleSchema?: boolean;
  breadcrumbSchema?: Array<{ name: string; url: string }>;
  faqSchema?: Array<{ question: string; answer: string }>;
}

// ---- Head tag helpers (runtime meta mutation) ----------------------------

function setMetaTag(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setHreflang(hreflang: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    `link[rel="alternate"][hreflang="${hreflang}"]`,
  );
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// ---- JSON-LD injection (kept separate from meta tag mutation) ------------

function setJsonLd(schemas: object[]) {
  document.head
    .querySelectorAll('script[data-readdy-seo="jsonld"]')
    .forEach((el) => el.remove());

  schemas.forEach((schema) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-readdy-seo', 'jsonld');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}

export function SEOHelmet({
  title,
  description,
  keywords,
  pathname = '/',
  lang = 'en',
  ogImage,
  articleSchema = false,
  breadcrumbSchema,
  faqSchema,
}: SEOProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_TITLE}` : SITE_TITLE;
    const fullUrl = `${SITE_URL}${pathname}`;
    const defaultDesc = t(
      'home_seo_description',
      'Explore CSSBuy Spreadsheet with guides, links, categories, and practical information for product research.',
    );
    const defaultKeywords = t(
      'home_seo_keywords',
      'CSSBuy Spreadsheet, CSSBuy links, CSSBuy guide, CSSBuy QC, CSSBuy shipping, CSSBuy coupon',
    );
    const desc = description || defaultDesc;
    const kw = keywords || defaultKeywords;
    const image = ogImage || DEFAULT_OG_IMAGE;
    const locale =
      lang === 'pt' ? 'pt_BR' : lang === 'de' ? 'de_DE' : 'en_US';

    // Title
    document.title = fullTitle;

    // Basic meta
    setMetaTag('name', 'description', desc);
    setMetaTag('name', 'keywords', kw);
    setMetaTag('name', 'robots', 'index, follow');
    setMetaTag('name', 'language', lang);
    setMetaTag('name', 'geo.region', 'BR');
    setMetaTag('name', 'geo.placename', 'Brazil');

    // Canonical + language alternates
    setCanonical(fullUrl);
    setHreflang('pt', `${SITE_URL}/pt${pathname}`);
    setHreflang('en', `${SITE_URL}/en${pathname}`);
    setHreflang('de', `${SITE_URL}/de${pathname}`);
    setHreflang('x-default', `${SITE_URL}/en${pathname}`);

    // Open Graph
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('property', 'og:description', desc);
    setMetaTag('property', 'og:url', fullUrl);
    setMetaTag('property', 'og:site_name', SITE_TITLE);
    setMetaTag('property', 'og:image', image);
    setMetaTag('property', 'og:locale', locale);

    // Twitter Card
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', fullTitle);
    setMetaTag('name', 'twitter:description', desc);
    setMetaTag('name', 'twitter:image', image);

    // ---- Schema.org JSON-LD ---------------------------------------------
    const schemas: object[] = [];

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_TITLE,
      url: SITE_URL,
      logo: SITE_LOGO,
      description: desc,
      sameAs: ['https://kaspreadsheet.com'],
    });

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_TITLE,
      url: SITE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    });

    const breadcrumbList = breadcrumbSchema || [
      { name: 'Home', url: SITE_URL },
    ];
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    });

    if (articleSchema) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title || SITE_TITLE,
        description: desc,
        url: fullUrl,
        author: {
          '@type': 'Organization',
          name: 'CSSBuy Spreadsheet Editorial Team',
        },
        publisher: {
          '@type': 'Organization',
          name: SITE_TITLE,
          logo: {
            '@type': 'ImageObject',
            url: SITE_LOGO,
          },
        },
        image: image,
        dateModified: new Date().toISOString().split('T')[0],
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': fullUrl,
        },
      });
    }

    if (faqSchema && faqSchema.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqSchema.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      });
    }

    setJsonLd(schemas);
  }, [
    title,
    description,
    keywords,
    pathname,
    lang,
    ogImage,
    articleSchema,
    breadcrumbSchema,
    faqSchema,
    t,
  ]);

  return null;
}